import { useEffect, useState, useCallback } from "react";
import { Link as RouterLink /* , useNavigate */ } from "react-router-dom"; // useNavigate comentado - usado no botão "Cadastrar em Lote"
import { useQuery } from "@tanstack/react-query";
import {
  getUsers,
  getUsersByCorbanId,
  resetPassword,
  updateUserStatus,
  blacklistUser,
  unblacklistUser,
} from "@/services/user.service";
import { useEnsureAuthenticated } from "@/contexts/ensureAuthenticated";
import { formatPhone } from "@/utils/formatPhone";
import { userPermissions } from "@/contexts/userPermissions";
import { PermissionEnum } from "@/utils/permissionEnum";

import { User } from "@/models/user.model";
import {
  Box,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Flex,
  Link,
  Button,
  useToast,
  IconButton,
  Select,
  useColorModeValue,
  Stack,
  HStack,
  Avatar,
  InputGroup,
  InputLeftElement,
  Input,
  Heading,
  Spinner,
} from "@chakra-ui/react";
import { documentFormatter } from "@/utils/documentFormatter";
import {
  FaFileExcel,
  FaUserFriends,
  FaUserPlus,
  // FaUsers, // Comentado - usado no botão "Cadastrar em Lote"
} from "react-icons/fa";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from "@chakra-ui/icons";
import * as XLSX from "xlsx";
import { RoleArr } from "@/models/role.model";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { CiSearch } from "react-icons/ci";
import { useDebounce } from "@/utils/useDebounce";
import { CreditPromoter } from "@/models/creditPromoter.model";
import { Corban } from "@/models/corban.model";
import { routes } from "@/routes";

interface UsersTableProps {
  corban?: Corban;
  creditPromoter?: CreditPromoter;
  isRegistryPage?: boolean;
  isRegisterFromPromoter?: boolean;
}

export default function UsersTable({ corban, creditPromoter, isRegistryPage, isRegisterFromPromoter }: UsersTableProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // const [corbanOwnerId, setCorbanIdFromRoute] = useState<string | undefined>(
  //   undefined
  // );
  // const navigate = useNavigate(); // Comentado - usado no botão "Cadastrar em Lote"
  const { user }: { user?: User } = useEnsureAuthenticated();
  const permissions = userPermissions(user);

  // console.log("corban:", creditPromoter);
  // console.log("corban:", corban);

  const toast = useToast();
  const backgroundStyle = useConditionalBackground();

  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User>();

  const [search, setSearch] = useState("");

  const corbanIdRef = corban?.id;
  const promoterIdRef = creditPromoter?.id;

  const debouncedSearch = useDebounce(search, 500);

  const handleSort = (field: string) => {
    setSortBy(field);
    setSortDirection(
      sortBy === field && sortDirection === "asc" ? "desc" : "asc"
    );
  };

  // useEffect(() => {
  //   setCorbanIdFromRoute(currentPath.split("/").pop());
  // }, [currentPath]);

  const fetchUsersData = useCallback(async () => {
    // console.log(
    //   'fetch user data >>>',
    //   corbanId,
    //   user,
    // )
    if (!user && !corbanIdRef && !promoterIdRef) {
      return { data: [], totalPages: 0 };
    }

    const isUsersFromPromoter = isRegisterFromPromoter && creditPromoter

    if (!isUsersFromPromoter && corbanIdRef) {
      const users = await getUsersByCorbanId(
        corbanIdRef,
        page,
        pageSize,
        sortBy,
        sortDirection,
        debouncedSearch.trim()
      );
      return users;
    }

    if (isUsersFromPromoter) {
      return await getUsers(
        page,
        pageSize,
        corbanIdRef,
        promoterIdRef,
        sortBy,
        sortDirection,
        debouncedSearch.trim()
      );
    }
  }, [page, pageSize, corbanIdRef, promoterIdRef, sortBy, sortDirection, debouncedSearch]);

  const staleSeconds = 20;

  const { data, isFetching, refetch } = useQuery({
    queryKey: [
      "users",
      page,
      pageSize,
      corbanIdRef,
      promoterIdRef,
      sortBy,
      sortDirection,
      debouncedSearch.trim()
    ],
    queryFn: fetchUsersData,
    staleTime: staleSeconds * 1000,
    enabled: !!user || !!corbanIdRef || !!promoterIdRef,
  });

  useEffect(() => {
    setPage(1);
  }, [corbanIdRef || promoterIdRef]);

  const usersData = data?.data || [];
  const totalPages = data?.totalPages || 1;

  const headerColor = useColorModeValue("white", "white");
  const rowHoverBg = useColorModeValue("teal.50", "teal.900");
  const rowBorderColor = useColorModeValue("gray.200", "gray.600");

  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const handleResetPassword = async (user: User) => {
    setIsLoading(true);
    setSelectedUser(user);
    try {
      await resetPassword(user);
      await delay(3000);
      toast({
        title: "Senha resetada com sucesso!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (erro) {
      toast({
        title: "Erro ao resetar a senha",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    const exportData = usersData.map((user: User) => ({
      Login: user.id,
      Nome: user.name,
      Sobrenome: user.lastName,
      CPF: user.document,
      Email: user.email,
      Celular: user.cellphone,
      Cargo: user.roles,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    const url = window.URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "users.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Arquivo Excel de cadastro baixado com sucesso.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const formatRole = (role: RoleArr): string => {
    if (
      role === "MASTER_CORBAN_SEM_PROMOTORA" ||
      role === "MASTER_CORBAN_COM_PROMOTORA"
    ) {
      return "Master Corban";
    }
    return role
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Função comentada - usada no botão "Cadastrar em Lote"
  // const handleAddBulk = () => {
  //   navigate(
  //     routes.find((route) => route.name === "user-insert-batch")?.path || "/"
  //   );
  // };

  const TotalusersData = data?.data || [];

  const handleToggleUserAccess = async (user: User, action: 'DEACTIVATE' | "REACTIVATE") => {
    try {
      if (!user.id) {
        throw new Error("ID do usuário é indefinido.");
      }

      if (!action) {
        throw new Error("Ação do usuário é indefinido.");
      }

      let updatedUser
      if (action === 'DEACTIVATE') {
        updatedUser = { ...user, enabled: false };
      } else if (action === 'REACTIVATE') {
        updatedUser = { ...user, enabled: true, firstLogin: false };
      }

      await updateUserStatus(updatedUser!);

      toast({
        title: action === 'REACTIVATE' ? "Usuário reativado com sucesso!" : "Usuário desativado com sucesso!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      await refetch();
    } catch (error) {
      console.error(action === 'REACTIVATE' ? "Erro ao reativar usuário" : "Erro ao desativar usuário:", error);
      toast({
        title: action === 'REACTIVATE' ? "Erro ao reativar usuário" : "Erro ao desativar usuário",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleToggleBlacklist = async (user: User, action: 'BLACKLIST' | 'UNBLACKLIST') => {
    try {
      if (!user.id) {
        throw new Error("ID do usuário é indefinido.");
      }

      if (action === 'BLACKLIST') {
        await blacklistUser(user.id);
      } else {
        await unblacklistUser(user.id);
      }

      toast({
        title: action === 'BLACKLIST' ? "Usuário adicionado à blacklist!" : "Usuário removido da blacklist!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      await refetch();
    } catch (error) {
      console.error(action === 'BLACKLIST' ? "Erro ao adicionar à blacklist:" : "Erro ao remover da blacklist:", error);
      toast({
        title: action === 'BLACKLIST' ? "Erro ao adicionar à blacklist" : "Erro ao remover da blacklist",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getUserQueryParams = () => {
    if (isRegisterFromPromoter) {
      return `?creditPromoterId=${promoterIdRef}`;
    }
    
    if (corbanIdRef) {
      return `?corbanId=${corbanIdRef}`;
    }
    if (promoterIdRef) {
      return `?creditPromoterId=${promoterIdRef}`;
    }
    return "";
  };


  return (
    <Box
      padding={{ base: "2", md: "4" }}
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
    >
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: 4, md: 6 }}
        color="white"
        boxShadow="md"
        width="100%"
        mb={2}
      >
        <Flex
          alignItems={{ base: "center", md: "center" }}
          justifyContent="space-between"
          flexDirection={{ base: "column", md: "row" }}
          textAlign={{ base: "center", md: "left" }}
        >
          <Flex alignItems="center" mb={{ base: 4, md: 0 }}>
            <FaUserFriends size="2rem" />
            <Text
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              ml={4}
              fontFamily="Lexend Deca"
            >
              {isRegisterFromPromoter && creditPromoter ? `Cadastro de Usuário internos da Promotora: ${creditPromoter?.businessName}` : !isRegisterFromPromoter && isRegistryPage && creditPromoter ? `Cadastro de Usuário internos da Promotora: ${creditPromoter.businessName}`: corban ? `Cadastro dos usários do CORBAN: ${corban.businessName}` : "Cadastro"}
            </Text>
          </Flex>

          <Flex
            flexDirection={{ base: "column", md: "row" }}
            alignItems="flex-end"
            mt={{ base: 4, md: 0 }}
            width="100%"
            justifyContent={{ base: "center", md: "flex-end" }}
            gap={4}
          >
            {permissions.some(
              (permission) => permission === PermissionEnum.CREATE_USER
            ) && (
                <Stack
                  direction={{ base: "column", md: "row" }}
                  spacing={4}
                  width="100%"
                  justifyContent={{ base: "center", md: "end" }}
                >
                  <Link
                    as={RouterLink}
                    to={{
                      pathname:
                        routes.find((route) => route.name === "user-create")
                          ?.path || "/users/create",
                      search: getUserQueryParams(),
                    }}
                    bg="white"
                    color="black"
                    px={4}
                    py={{ base: 4, md: 2 }}
                    borderRadius="md"
                    fontWeight="bold"
                    _hover={{ bg: "gray.200" }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize={{ base: "lg", md: "md" }}
                    height={{ base: "3.5rem", md: "2.5rem" }}
                    width={{ base: "100%", md: "auto" }}
                    fontFamily="Lexend Deca"
                  >
                    <FaUserPlus
                      style={{ marginRight: "0.5rem", fontSize: "1.25rem" }}
                    />
                    Cadastrar Usuário
                  </Link>
                  {/* Botão "Cadastrar em Lote" comentado para possível uso futuro */}
                  {/* <Button
                    bg="white"
                    color="black"
                    px={4}
                    py={{ base: 4, md: 2 }}
                    borderRadius="md"
                    fontWeight="bold"
                    _hover={{ bg: "gray.200" }}
                    onClick={handleAddBulk}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize={{ base: "lg", md: "md" }}
                    height={{ base: "3.5rem", md: "2.5rem" }}
                    width={{ base: "100%", md: "auto" }}
                    fontFamily="Lexend Deca"
                  >
                    <FaUsers style={{ marginRight: "0.5rem" }} />
                    Cadastrar em Lote
                  </Button> */}
                </Stack>
              )}
            <HStack spacing={2} align={"flex-start"}>
              <IconButton
                icon={<FaFileExcel />}
                aria-label="Download Excel"
                onClick={handleDownload}
                colorScheme="green"
              />
            </HStack>
          </Flex>
        </Flex>

        <Flex w={{ base: "100%", md: "30%" }} mt={8} gap={2}>
          <InputGroup>
            <InputLeftElement mt={{ base: 2, md: 1 }} pointerEvents="none">
              <CiSearch color="black" size="2rem" />
            </InputLeftElement>
            <Input
              height={{ base: "3.5rem ", md: "3rem" }}
              bg={"white"}
              placeholder="Pesquisar..."
              variant="filled"
              type="text"
              maxLength={30}
              color="black"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              _focus={{ bg: "white" }}
              _hover={{ bg: "white" }}
              fontFamily="Lexend Deca"
            />
          </InputGroup>
        </Flex>
      </Box>

      {isFetching ? (
        <Flex justify="center" align="center" height="200px">
          <Spinner
            size="xl"
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
          />
        </Flex>
      ) : TotalusersData.length ? (
        <>
          <Flex
            justify="center"
            alignItems="center"
            mt={1}
            mb={2}
            flexDirection={{ base: "column", md: "row" }}
            width="100%"
            px={{ base: 2, md: 0 }}
          >
            <Flex alignItems="center" mb={{ base: 2, md: 2 }}>
              <IconButton
                icon={<ChevronLeftIcon />}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                isDisabled={page === 1}
                mr={2}
                aria-label="Página Anterior"
                size={{ base: "sm", md: "sm" }}
              />
              <Text mx={4}>
                Página {page} de {totalPages}
              </Text>
              <IconButton
                icon={<ChevronRightIcon />}
                onClick={() =>
                  setPage((prev) => Math.min(prev + 1, totalPages))
                }
                isDisabled={page === totalPages}
                ml={2}
                aria-label="Próxima Página"
                size={{ base: "sm", md: "sm" }}
              />
            </Flex>

            <Flex alignItems="center" mb={2} ml={4}>
              <Text mr={2}>Itens por página:</Text>
              <Select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                width={{ base: "100%", md: "auto" }}
                size={{ base: "md", md: "sm" }}
                fontSize={{ base: "lg", md: "md" }}
                fontFamily="Lexend Deca"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Select>
            </Flex>
          </Flex>
          <Box width="100%" overflow="visible">
            <TableContainer borderRadius="md" boxShadow="md" width="100%">
              <Table size="sm">
                <Thead
                  {...backgroundStyle}
                  position="sticky"
                  top={0}
                  zIndex={1}
                  boxShadow="md"
                >
                  <Tr>
                    <Th
                      color={headerColor}
                      textAlign="center"
                      fontSize="xs"
                      py={5}
                    >
                      <HStack justify="center">
                        <Text>AÇÕES</Text>
                      </HStack>
                    </Th>
                    <Th
                      color={headerColor}
                      textAlign="center"
                      fontSize="xs"
                      py={5}
                    >
                      <HStack justify="center">
                        <Text>LOGIN</Text>
                        <IconButton
                          icon={
                            sortBy === "id" && sortDirection === "asc" ? (
                              <ChevronUpIcon />
                            ) : (
                              <ChevronDownIcon />
                            )
                          }
                          variant="ghost"
                          size="1"
                          fontSize={"1rem"}
                          borderRadius={"lg"}
                          aria-label="Sort by Id"
                          bg={"gray.200"}
                          onClick={() => handleSort("id")}
                        />
                      </HStack>
                    </Th>
                    <Th
                      color={headerColor}
                      textAlign="center"
                      fontSize="xs"
                      py={5}
                    >
                      <HStack justify="center">
                        <Text>NOME</Text>
                        <IconButton
                          icon={
                            sortBy === "name" && sortDirection === "asc" ? (
                              <ChevronUpIcon />
                            ) : (
                              <ChevronDownIcon />
                            )
                          }
                          variant="ghost"
                          size="1"
                          fontSize={"1rem"}
                          borderRadius={"lg"}
                          aria-label="Sort by Name"
                          bg={"gray.200"}
                          onClick={() => handleSort("name")}
                        />
                      </HStack>
                    </Th>
                    <Th
                      color={headerColor}
                      textAlign="center"
                      fontSize="xs"
                      py={5}
                    >
                      <HStack justify="center">
                        <Text>CPF</Text>
                        <IconButton
                          icon={
                            sortBy === "document" && sortDirection === "asc" ? (
                              <ChevronUpIcon />
                            ) : (
                              <ChevronDownIcon />
                            )
                          }
                          variant="ghost"
                          size="1"
                          fontSize={"1rem"}
                          borderRadius={"lg"}
                          aria-label="Sort by Document"
                          bg={"gray.200"}
                          onClick={() => handleSort("document")}
                        />
                      </HStack>
                    </Th>
                    <Th
                      color={headerColor}
                      textAlign="center"
                      fontSize="xs"
                      py={5}
                    >
                      <HStack justify="center">
                        <Text>EMAIL</Text>
                        <IconButton
                          icon={
                            sortBy === "email" && sortDirection === "asc" ? (
                              <ChevronUpIcon />
                            ) : (
                              <ChevronDownIcon />
                            )
                          }
                          variant="ghost"
                          size="1"
                          fontSize={"1rem"}
                          borderRadius={"lg"}
                          aria-label="Sort by Email"
                          bg={"gray.200"}
                          onClick={() => handleSort("email")}
                        />
                      </HStack>
                    </Th>
                    <Th
                      color={headerColor}
                      textAlign="center"
                      fontSize="xs"
                      py={5}
                    >
                      <HStack justify="center">
                        <Text>CELULAR</Text>
                        <IconButton
                          icon={
                            sortBy === "cellphone" &&
                              sortDirection === "asc" ? (
                              <ChevronUpIcon />
                            ) : (
                              <ChevronDownIcon />
                            )
                          }
                          variant="ghost"
                          size="1"
                          fontSize={"1rem"}
                          borderRadius={"lg"}
                          aria-label="Sort by Cellphone"
                          bg={"gray.200"}
                          onClick={() => handleSort("cellphone")}
                        />
                      </HStack>
                    </Th>
                    <Th
                      color={headerColor}
                      textAlign="center"
                      fontSize="xs"
                      py={5}
                    >
                      <HStack justify="center">
                        <Text>CARGO</Text>
                        <IconButton
                          icon={
                            sortBy === "UsersRoles" &&
                              sortDirection === "asc" ? (
                              <ChevronUpIcon />
                            ) : (
                              <ChevronDownIcon />
                            )
                          }
                          variant="ghost"
                          size="1"
                          fontSize={"1rem"}
                          borderRadius={"lg"}
                          aria-label="Sort by Roles"
                          bg={"gray.200"}
                          onClick={() => handleSort("UsersRoles")}
                        />
                      </HStack>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {TotalusersData.map((userItem: User, index: number) => (
                    <Tr
                      key={index}
                      _hover={{ bg: rowHoverBg }}
                      transition="all 0.3s ease-in-out"
                      borderBottom="1px solid"
                      borderColor={rowBorderColor}
                      fontFamily="Lexend Deca"
                    >
                      <Td
                        textAlign="center"
                        fontSize={{ base: "lg", md: "sm" }}
                        py={{ base: 2, md: 3 }}
                        fontFamily="Lexend Deca"
                      >
                        <Flex justify="center" flexDirection="column" gap={2}>
                          <Button
                            bg="#2b5b83"
                            size={{ base: "md", md: "md" }}
                            colorScheme="teal"
                            onClick={() => handleResetPassword(userItem)}
                            width="100%"
                            py={{ base: 2.5, md: 2 }}
                            fontSize={{ base: "md", md: "sm" }}
                            fontWeight="bold"
                            fontFamily="Lexend Deca"
                            isDisabled={
                              isLoading && selectedUser?.id === userItem.id
                            }
                            loadingText="Carregando..."
                          >
                            Reset de Senha
                          </Button>
                          {
                            permissions.some(permission =>
                              [PermissionEnum.GRANATECH, PermissionEnum.ADMIN, PermissionEnum.EDIT_USER].includes(permission)
                            ) && userItem?.enabled ? (
                              <Button
                                size={{ base: "md", md: "md" }}
                                colorScheme="red"
                                onClick={() => handleToggleUserAccess(userItem, 'DEACTIVATE')}
                                width="100%"
                                py={{ base: 2.5, md: 2 }}
                                fontSize={{ base: "md", md: "sm" }}
                                fontWeight="bold"
                                fontFamily="Lexend Deca"
                              >
                                Desativar Usuário
                              </Button>
                            ) : (
                              <Button
                                size={{ base: "md", md: "md" }}
                                colorScheme="green"
                                onClick={() => handleToggleUserAccess(userItem, 'REACTIVATE')}
                                width="100%"
                                py={{ base: 2.5, md: 2 }}
                                fontSize={{ base: "md", md: "sm" }}
                                fontWeight="bold"
                                fontFamily="Lexend Deca"
                              >
                                Reativar Usuário
                              </Button>
                            )
                          }
                          {
                            permissions.some(permission =>
                              [PermissionEnum.GRANATECH, PermissionEnum.ADMIN, PermissionEnum.EDIT_USER].includes(permission)
                            ) && (
                              userItem?.disabledByAdmin ? (
                                <Button
                                  size={{ base: "md", md: "md" }}
                                  colorScheme="teal"
                                  onClick={() => handleToggleBlacklist(userItem, 'UNBLACKLIST')}
                                  width="100%"
                                  py={{ base: 2.5, md: 2 }}
                                  fontSize={{ base: "md", md: "sm" }}
                                  fontWeight="bold"
                                  fontFamily="Lexend Deca"
                                >
                                  Remover da Blacklist
                                </Button>
                              ) : (
                                <Button
                                  size={{ base: "md", md: "md" }}
                                  colorScheme="orange"
                                  onClick={() => handleToggleBlacklist(userItem, 'BLACKLIST')}
                                  width="100%"
                                  py={{ base: 2.5, md: 2 }}
                                  fontSize={{ base: "md", md: "sm" }}
                                  fontWeight="bold"
                                  fontFamily="Lexend Deca"
                                >
                                  Adicionar à Blacklist
                                </Button>
                              )
                            )
                          }
                        </Flex>
                      </Td>
                      <Td
                        textAlign="center"
                        fontSize={{ base: "lg", md: "sm" }}
                        py={{ base: 2, md: 3 }}
                      >
                        {permissions.some(
                          (permission) =>
                            permission === PermissionEnum.READ_USER
                        ) ? (
                          <Link
                            as={RouterLink}
                            to={`/users/edit/${userItem.id}`}
                            color="blue.500"
                            _hover={{ textDecoration: "underline" }}
                            fontSize={{ base: "lg", md: "sm" }}
                            fontFamily="Lexend Deca"
                          >
                            {userItem.id}
                          </Link>
                        ) : (
                          userItem.id
                        )}
                      </Td>
                      <Td
                        textAlign="center"
                        fontSize="sm"
                        py={{ base: 2, md: 3 }}
                        verticalAlign="middle"
                      >
                        <Flex align="center" mr={5}>
                          <Avatar size="sm" />
                          <Text textAlign="center" w="100%">
                            {userItem.name} {userItem.lastName}
                          </Text>
                        </Flex>
                      </Td>
                      <Td
                        textAlign="center"
                        fontSize={{ base: "lg", md: "sm" }}
                        py={{ base: 2, md: 3 }}
                        fontFamily="Lexend Deca"
                      >
                        {userItem.document
                          ? documentFormatter(userItem.document)
                          : "-"}
                      </Td>
                      <Td
                        textAlign="center"
                        fontSize={{ base: "lg", md: "sm" }}
                        py={{ base: 2, md: 3 }}
                        fontFamily="Lexend Deca"
                      >
                        {userItem.email || "-"}
                      </Td>
                      <Td
                        textAlign="center"
                        fontSize={{ base: "lg", md: "sm" }}
                        py={{ base: 2, md: 3 }}
                        fontFamily="Lexend Deca"
                      >
                        {userItem.cellphone
                          ? formatPhone(userItem.cellphone)
                          : "-"}
                      </Td>
                      <Td
                        textAlign="center"
                        fontSize={{ base: "lg", md: "sm" }}
                        py={{ base: 2, md: 3 }}
                        fontFamily="Lexend Deca"
                      >
                        {userItem.roles?.[0]?.name
                          ? formatRole(userItem.roles[0].name as RoleArr)
                          : "-"}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </>
      ) : (
        <Heading mt={100} textAlign={"center"} color="gray.600">
          Nenhum Usuário Encontrado
        </Heading>
      )}
      <Flex
        justify="center"
        alignItems="center"
        flexDirection={{ base: "column", md: "row" }}
        width="100%"
        px={{ base: 2, md: 0 }}
      >
        {TotalusersData.length ? (
          <>
            <Flex alignItems="center" mt={4} mb={{ base: 2, md: 2 }}>
              <IconButton
                icon={<ChevronLeftIcon />}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                isDisabled={page === 1}
                mr={2}
                aria-label="Página Anterior"
                size={{ base: "sm", md: "sm" }}
              />
              <Text mx={4}>
                Página {page} de {totalPages}
              </Text>
              <IconButton
                icon={<ChevronRightIcon />}
                onClick={() =>
                  setPage((prev) => Math.min(prev + 1, totalPages))
                }
                isDisabled={page === totalPages}
                ml={2}
                aria-label="Próxima Página"
                size={{ base: "sm", md: "sm" }}
              />
            </Flex>
            <Box ml={{ base: 0, md: 4 }} mb={2} mt={{ base: 2, md: 4 }}>
              <Flex alignItems="center">
                <Text mr={2}>Itens por página:</Text>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  width={{ base: "100%", md: "auto" }}
                  size={{ base: "md", md: "sm" }}
                  fontSize={{ base: "lg", md: "md" }}
                  fontFamily="Lexend Deca"
                >
                  <option value={10}>10 </option>
                  <option value={20}>20 </option>
                  <option value={50}>50 </option>
                  <option value={100}>100 </option>
                </Select>
              </Flex>
            </Box>
          </>
        ) : null}
      </Flex>
    </Box>
  );
}